import { CockpitStats, ICockpitStats } from '../../../backend/backendapi';
import SignalRHandler from '../../../../util/SignalRHandler';
import { HubConnectionState } from '@microsoft/signalr';
import { Like } from '../../../../util/Like';

export default class CockpitSignalRHandler extends SignalRHandler {
  statsToday = CockpitStats.fromJS({ from: new Date(), to: new Date(), checkedInCount: 0, totalCount: 0 } as ICockpitStats);
  statsPastMonths = CockpitStats.fromJS({ from: new Date(), to: new Date(), checkedInCount: 0, totalCount: 0 } as ICockpitStats);

  private from: Date;
  private to: Date;

  private statsTodayFrom: Date;
  private statsTodayTo: Date;

  private statsPastMonthsFrom: Date;
  private statsPastMonthsTo: Date;

  private onUpdateCockpitFn: () => void = () => undefined;

  constructor(from: Date, to: Date, statsToday: Date) {
    super('hubs/visitorManagement',
      connection => {
        connection.invoke('SubscribeCockpit', this.from, this.to);
        connection.invoke('SubscribeStats', this.statsTodayFrom, this.statsTodayTo);
        connection.invoke('SubscribeStats', this.statsPastMonthsFrom, this.statsPastMonthsTo);
      },
      {
        'UpdateCockpit': async () => {
          this.onUpdateCockpitFn();
        },
        'UpdateStats': async (data: Like<CockpitStats>) => {
          const stats = CockpitStats.fromJS(data);
          if (stats.from.getTime() === this.statsPastMonthsFrom.getTime() && stats.to.getTime() === this.statsPastMonthsTo.getTime()) {
            this.statsPastMonths = CockpitStats.fromJS(stats);
          } else if (stats.from.getTime() === this.statsTodayFrom.getTime() && stats.to.getTime() === this.statsTodayTo.getTime()) {
            this.statsToday = CockpitStats.fromJS(stats);
          }
        },
      });
    this.from = from;
    this.to = to;
    this.statsTodayFrom = this.computeStatsTodayFromDate(statsToday);
    this.statsTodayTo = this.computeStatsTodayToDate(statsToday);
    this.statsPastMonthsFrom = this.computeStatsPastMonthsFromDate(statsToday);
    this.statsPastMonthsTo = this.computeStatsPastMonthsToDate(statsToday);
  }

  onUpdateCockpit(fn: () => void) {
    this.onUpdateCockpitFn = fn;
  };

  updateDateRange(from: Date, to: Date): void {
    const connected = this.connection?.state === HubConnectionState.Connected;
    if (connected) {
      this.connection?.invoke('UnsubscribeCockpit', this.from, this.to);
    }
    this.from = from;
    this.to = to;
    if (connected) {
      this.connection?.invoke('SubscribeCockpit', this.from, this.to);
    }
  }

  updateStatsDate(date: Date): void {
    const newFrom = this.computeStatsTodayFromDate(date);
    const newTo = this.computeStatsTodayToDate(date);
    if (newFrom.getTime() === this.statsTodayFrom.getTime()
      && newTo.getTime() === this.statsTodayTo.getTime()) {
      return;
    }

    const connected = this.connection?.state === HubConnectionState.Connected;
    if (connected) {
      this.connection?.invoke('UnsubscribeStats', this.statsTodayFrom, this.statsTodayTo);
      this.connection?.invoke('UnsubscribeStats', this.statsPastMonthsFrom, this.statsPastMonthsTo);
    }

    this.statsTodayFrom = newFrom;
    this.statsTodayTo = newTo;
    this.statsPastMonthsFrom = this.computeStatsPastMonthsFromDate(date);
    this.statsPastMonthsTo = this.computeStatsPastMonthsToDate(date);

    if (connected) {
      this.connection?.invoke('SubscribeStats', this.statsTodayFrom, this.statsTodayTo);
      this.connection?.invoke('SubscribeStats', this.statsPastMonthsFrom, this.statsPastMonthsTo);
    }
  }

  private computeStatsPastMonthsFromDate(date: Date): Date {
    const from = this.computeStatsPastMonthsToDate(date);
    from.setMonth(from.getMonth() - 3);
    return from;
  }

  private computeStatsPastMonthsToDate(date: Date): Date {
    return this.computeStatsTodayToDate(date);
  }

  private computeStatsTodayFromDate(date: Date): Date {
    const from = new Date(date);
    from.setHours(0, 0, 0, 0);
    return from;
  }

  private computeStatsTodayToDate(date: Date): Date {
    const to = this.computeStatsTodayFromDate(date);
    to.setDate(to.getDate() + 1);
    return to;
  }
}
